import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import ProjectDate from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/dates/project-date';
import Container from '@ui/container/container';
import { Session } from '@/types/session.types';
import { useAppDispatch } from '@/store/hooks';
import { DateRange } from '@ui/calendar/types';
import { saveWorkingProjectByFields, saveWorkingProjectSessionByField } from '@/store/slices/project';

type TFocusedSessionInfoContentProps = {
  session?: Session | null;
  selectedDates: {
    startDate: number | null;
    endDate: number | null;
    selectedTimezone: string;
  };
  isFocusedWithin: boolean;
  setSelectedDates: Dispatch<
    SetStateAction<{
      startDate: number | null;
      endDate: number | null;
      selectedTimezone: string;
    }>
  >;
  syncProjectDates?: boolean;
};

const FocusedSessionInfoContent = ({
  session,
  selectedDates,
  setSelectedDates,
  isFocusedWithin,
  syncProjectDates,
}: TFocusedSessionInfoContentProps) => {
  const dispatch = useAppDispatch();

  const startTimestamp = session?.timestamp;
  const endTimestamp = session?.end_timestamp;

  const handleDateChange = useCallback(
    ({ startDate, endDate }: DateRange) => {
      if (!startDate || !endDate || !session) return;

      // Update the project dates in the store or database
      setSelectedDates((prev) => ({ startDate: null, endDate: null, selectedTimezone: prev.selectedTimezone }));

      dispatch(
        saveWorkingProjectSessionByField({
          uuid: session.uuid,
          timestamp: startDate,
          end_timestamp: endDate,
          requires_time_change: null,
        })
      );

      if (syncProjectDates) {
        dispatch(
          saveWorkingProjectByFields({
            start_timestamp: startDate,
            end_timestamp: endDate,
          })
        );
      }
    },
    [dispatch, session, setSelectedDates, syncProjectDates]
  );

  useEffect(() => {
    if (!isFocusedWithin && selectedDates.startDate && selectedDates.endDate) {
      // we are leaving the focused state of session info, save the date changes
      handleDateChange(selectedDates);
    }
  }, [handleDateChange, isFocusedWithin, selectedDates]);

  if (!session || !isFocusedWithin) return null;

  return (
    <Container
      width="100%"
      height="84px"
      className="flex-column ai-center gap-4"
      opacity={isFocusedWithin ? 1 : 0}
      transitionDuration="0ms"
      data-component="FocusedSessionInfoContent"
    >
      <Container className="flex flex-1" width="100%">
        <ProjectDate
          wrapperClass="width-100-percent"
          hideLabel
          timestamp={startTimestamp}
          end_timestamp={endTimestamp}
          selectedTimezone={selectedDates.selectedTimezone}
          isInEditMode={isFocusedWithin}
          onChange={setSelectedDates}
          requiresTimeChange={session?.requires_time_change}
          onClose={() => {
            if (selectedDates?.startDate && selectedDates.endDate) {
              handleDateChange({
                startDate: selectedDates.startDate,
                endDate: selectedDates.endDate,
                selectedTimezone: selectedDates.selectedTimezone,
              });
            }
          }}
        />
      </Container>
    </Container>
  );
};

export default FocusedSessionInfoContent;
